import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  SimpleGrid,
  Card,
  Text,
  Badge,
  Stack,
  Group,
  Loader,
  Alert,
  Button,
  Avatar
} from '@mantine/core';
import {
  IconBuildingStore,
  IconExclamationCircle,
  IconMail,
  IconMapPin,
  IconUser
} from '@tabler/icons-react';
import { api } from '../services/api';

const SellersPage = () => {
  const navigate = useNavigate();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/princesses/');
      setSellers(response.sellers);
    } catch (err) {
      console.error('Error fetching sellers:', err);
      setError(err.response?.data?.error || 'Failed to load princesses');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Title order={1}>Verified Princesses</Title>

        {error && (
          <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
            {error}
          </Alert>
        )}

        {sellers.length === 0 && !loading ? (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Text c="dimmed" size="lg">No princesses found</Text>
              <Text c="dimmed" mt="xs">Check back later for new princesses</Text>
            </div>
          </Card>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {sellers.map((seller) => (
              <Card 
                key={seller.id} 
                shadow="sm" 
                padding="lg" 
                radius="md" 
                withBorder
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <Stack gap="md" style={{ flex: 1 }}>
                  {/* Seller Header with Avatar */}
                  <Group gap="sm">
                    <Avatar
                      src={seller.primary_profile_image}
                      size="lg"
                      radius="md"
                      color="blue"
                    >
                      <IconUser size="1.5rem" />
                    </Avatar>
                    <div style={{ flex: 1 }}>
                      <Text fw={500} size="lg">{seller.business_display_name}</Text>
                      <Text size="sm" c="dimmed">@{seller.user_username}</Text>
                    </div>
                    {seller.is_verified && (
                      <Badge color="green" variant="light" size="sm">
                        Verified
                      </Badge>
                    )}
                  </Group>

                  {/* Description */}
                  <Text size="sm" lineClamp={3} style={{ flex: 1 }}>
                    {seller.business_description || 'No description available'}
                  </Text>

                  {/* Location */}
                  <Group gap="xs">
                    <IconMapPin size="1rem" color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="dimmed">
                      {seller.business_city}, {seller.business_state}
                    </Text>
                  </Group>

                  {/* Stats */}
                  <Group grow>
                    <Stack gap={2} align="center">
                      <Text size="xs" c="dimmed">Rating</Text>
                      <Text fw={500}>
                        {seller.average_rating > 0 ? `${seller.average_rating}/5` : 'New'}
                      </Text>
                    </Stack>
                    <Stack gap={2} align="center">
                      <Text size="xs" c="dimmed">Orders</Text>
                      <Text fw={500}>{seller.total_orders}</Text>
                    </Stack>
                    <Stack gap={2} align="center">
                      <Text size="xs" c="dimmed">Reviews</Text>
                      <Text fw={500}>{seller.total_reviews}</Text>
                    </Stack>
                  </Group>

                  {/* Action */}
                  <Button
                    variant="light"
                    fullWidth
                    leftSection={<IconUser size="1rem" />}
                    onClick={() => navigate(`/princess/${seller.id}`)}
                  >
                    View Princess
                  </Button>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Container>
  );
};

export default SellersPage;